import { useMemo, useState } from 'react'
import { useMutation } from '@tanstack/react-query'
import { OWNER_TIMEZONE, clientName, type Booking, type Client } from '@diana/shared'
import { api, ApiError, type CopyWeekResult } from '../../lib/api.js'
import { addDaysIso, weekDays, weekTitle } from '../../lib/datetime.js'
import { Button, ErrorLine, Rows } from '../../classroom/ui.js'
import { Sheet, SheetSection, plural } from './calendarUi.js'

type Props = {
  /** Monday of the week being copied. */
  sourceMonday: string
  /** Every booking already loaded for that week. */
  bookings: Booking[]
  clients: Client[]
  onClose: () => void
  onDone: () => void
}

/** What the server will actually carry over — mirrored here so counts match. */
const COPYABLE = new Set(['confirmed', 'completed'])

const CHECK = 'h-4 w-4 shrink-0 cursor-pointer accent-class-sky-700'

/**
 * Copy this week's timetable onto the next one.
 *
 * The checkbox list is the whole point: a week is a rhythm that mostly
 * repeats, minus the students who stopped. Diana unticks those, and only
 * the rest come across.
 */
export function CopyWeekDialog({ sourceMonday, bookings, clients, onClose, onDone }: Props) {
  const targetMonday = addDaysIso(sourceMonday, 7)

  /** Students who actually have lessons this week, with how many. */
  const roster = useMemo(() => {
    const byClient = new Map<string, { client: Client; lessons: number }>()
    let unassigned = 0

    for (const b of bookings) {
      if (!COPYABLE.has(b.status)) continue
      if (!b.clientId) {
        unassigned++
        continue
      }
      const client = clients.find((c) => c.id === b.clientId)
      if (!client) continue
      const row = byClient.get(client.id) ?? { client, lessons: 0 }
      row.lessons++
      byClient.set(client.id, row)
    }

    return {
      rows: [...byClient.values()].sort((a, b) => clientName(a.client).localeCompare(clientName(b.client), 'pl')),
      unassigned,
    }
  }, [bookings, clients])

  const [picked, setPicked] = useState<Set<string>>(() => new Set(roster.rows.map((r) => r.client.id)))
  const [withWindows, setWithWindows] = useState(true)
  const [result, setResult] = useState<CopyWeekResult | null>(null)

  const copy = useMutation({
    mutationFn: () =>
      api.studio.copyWeek({
        from: sourceMonday,
        to: targetMonday,
        windows: withWindows,
        clientIds: [...picked],
      }),
    onSuccess: (res) => {
      setResult(res)
      onDone()
    },
  })

  const toggle = (id: string) =>
    setPicked((prev) => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })

  const allPicked = picked.size === roster.rows.length && roster.rows.length > 0
  const lessonsToCopy = roster.rows.filter((r) => picked.has(r.client.id)).reduce((sum, r) => sum + r.lessons, 0)

  return (
    <Sheet
      label="Kopiuj tydzień"
      title="Kopiuj tydzień"
      sub={`${weekTitle(weekDays(sourceMonday))} → ${weekTitle(weekDays(targetMonday))}`}
      onClose={onClose}
    >
      {result ? (
        <Summary result={result} onClose={onClose} />
      ) : (
        <>
          <label className="flex cursor-pointer items-start gap-3">
            <input type="checkbox" checked={withWindows} onChange={(e) => setWithWindows(e.target.checked)} className={`${CHECK} mt-1`} />
            <span className="leading-snug">
              <span className="block font-semibold">Godziny otwarte na zapisy</span>
              <span className="block text-class-gloss text-class-ink-soft">Te same godziny będą wolne na stronie w przyszłym tygodniu.</span>
            </span>
          </label>

          <SheetSection
            title="Uczniowie"
            action={
              roster.rows.length > 0 && (
                <button
                  type="button"
                  className="cl-link"
                  onClick={() => setPicked(allPicked ? new Set() : new Set(roster.rows.map((r) => r.client.id)))}
                >
                  {allPicked ? 'Odznacz wszystkich' : 'Zaznacz wszystkich'}
                </button>
              )
            }
          >
            <p className="m-0 -mt-1 text-class-gloss text-class-ink-soft">
              Odznacz tych, którzy już z Tobą nie pracują — ich lekcje nie przejdą dalej.
            </p>

            {roster.rows.length === 0 ? (
              <p className="m-0 text-class-ink-soft">W tym tygodniu nie ma lekcji z uczniami do skopiowania.</p>
            ) : (
              <Rows>
                {roster.rows.map(({ client, lessons }) => {
                  const on = picked.has(client.id)
                  return (
                    <label key={client.id} className="flex min-h-14 cursor-pointer items-center gap-3 hover:bg-class-field-soft/50">
                      <input type="checkbox" checked={on} onChange={() => toggle(client.id)} className={CHECK} />
                      <span className={['min-w-0 flex-1 leading-snug', on ? '' : 'text-class-ink-faint'].join(' ')}>
                        <span className="font-semibold">{clientName(client)}</span>
                        {client.tags.length > 0 && <span className="ml-2 text-class-label text-class-ink-faint">{client.tags.join(', ')}</span>}
                      </span>
                      <span className="text-class-gloss text-class-ink-soft">
                        {lessons} {plural(lessons, 'lekcja', 'lekcje', 'lekcji')}
                      </span>
                    </label>
                  )
                })}
              </Rows>
            )}

            {roster.unassigned > 0 && (
              <p className="m-0 text-class-gloss text-class-ink-soft">
                {roster.unassigned} {roster.unassigned === 1 ? 'zapis ze strony nie zostanie' : 'zapisy ze strony nie zostaną'} skopiowanych — to
                konkretne osoby, które zarezerwowały jeden termin. Powiąż je z uczniem, jeśli mają wracać co tydzień.
              </p>
            )}
          </SheetSection>

          {copy.error instanceof ApiError && <ErrorLine>Nie udało się skopiować. Spróbuj jeszcze raz.</ErrorLine>}

          <div className="flex flex-wrap items-center gap-4">
            <Button onClick={() => copy.mutate()} disabled={copy.isPending || (!withWindows && picked.size === 0)}>
              {copy.isPending ? 'Kopiuję…' : 'Kopiuj do przyszłego tygodnia'}
            </Button>
            <span className="text-class-gloss text-class-ink-soft">
              {lessonsToCopy} {plural(lessonsToCopy, 'lekcja', 'lekcje', 'lekcji')}
              {withWindows && ' · godziny'}
            </span>
          </div>
        </>
      )}
    </Sheet>
  )
}

function Summary({ result, onClose }: { result: CopyWeekResult; onClose: () => void }) {
  const nothing = result.lessonsCopied === 0 && result.windowsCopied === 0 && result.lessonsSkipped === 0

  return (
    <>
      <p className={['m-0 font-semibold', nothing ? 'text-class-ink-soft' : 'text-class-correct'].join(' ')}>
        {nothing ? 'Nic do skopiowania.' : '✓ Skopiowane.'}
      </p>

      <Rows>
        <p className="m-0 flex min-h-12 items-center">
          <strong className="mr-1.5 font-bold">{result.lessonsCopied}</strong> lekcji przeniesionych
        </p>
        <p className="m-0 flex min-h-12 flex-wrap items-center">
          <strong className="mr-1.5 font-bold">{result.windowsCopied}</strong> godzin otwartych na zapisy
          {result.windowsSkipped > 0 && <span className="text-class-ink-soft">&nbsp;· {result.windowsSkipped} już było</span>}
        </p>
        {result.lessonsSkipped > 0 && (
          <p className="m-0 flex min-h-12 items-center text-class-wrong">
            ✗&nbsp;<strong className="mr-1.5 font-bold">{result.lessonsSkipped}</strong> pominiętych — ten czas był już zajęty
          </p>
        )}
      </Rows>

      {result.skipped.length > 0 && (
        <ul className="m-0 flex list-none flex-col gap-1 p-0 text-class-gloss text-class-ink-soft">
          {result.skipped.map((s, i) => (
            <li key={i} className="first-letter:uppercase">
              {s.name} — {s.when}
            </li>
          ))}
        </ul>
      )}

      <p className="m-0 text-class-gloss text-class-ink-soft">
        Skopiowane lekcje są potwierdzone i mają tę samą cenę co pierwowzór. Notatki nie przechodzą. Strefa czasowa: {OWNER_TIMEZONE}.
      </p>

      <div>
        <Button onClick={onClose}>Gotowe</Button>
      </div>
    </>
  )
}
